// ChatWindow.jsx (Optimized for Layout)
import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import moment from "moment";
import { SOCKET_CLIENT } from "../../../../utils/socket";
import { formatDateSeparator, getUser } from "../../../../utils/commonfunction";
import { ROLE } from "../../../../utils/Constants";
import { getAdminChat } from "../../services/chat.services";

// Destructure the role constant from the original file's context
const SENDER = getUser(ROLE.SUPER_ADMIN);

/**
 * Renders the active chat window for a selected user.
 * @param {object} props
 * @param {object} props.activeChat - Contains receiver details and room info.
 */
export default function ChatWindow({ activeChat }) {
  const { roomID, receiverDetails } = activeChat;

  const [messages, setMessages] = useState([]);
  const chatWindowRef = useRef();

  // Formik for sending messages
  const messageForm = useFormik({
    initialValues: {
      message: "",
      senderId: SENDER._id,
      receiverId: receiverDetails?._id || "",
      roomID: roomID || "",
      type: "text",
    },
    enableReinitialize: true,
    onSubmit: (values, { resetForm }) => {
      if (!values.message.trim()) return;
      SOCKET_CLIENT.emit("SEND-MESSAGE", values);
      resetForm();
    },
  });

  // Scroll to bottom
  const scrollToBottom = () => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTo({
        top: chatWindowRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  // Join room, get chat history, and socket listener (all combined into one effect)
  useEffect(() => {
    if (!roomID || !receiverDetails?._id) {
      setMessages([]);
      return;
    }

    console.log(`Joining Room: ${roomID}`);
    // 1. Join room
    SOCKET_CLIENT.emit("JOIN-USER", { roomID, userID: SENDER?._id });

    // 2. Get chat history
    const fetchMessages = async () => {
      const formData = new FormData();
      formData.append("roomID", roomID);

      try {
        const response = await getAdminChat(formData);
        if (response?.data?.list) {
          setMessages(response.data.list);
          setTimeout(scrollToBottom, 100);
        }
      } catch (err) {
        console.error("Error fetching chat:", err);
      }
    };
    fetchMessages();

    // 3. Handle new message from socket
    const handleNewMessage = (message) => {
      if (message?.roomID === roomID) {
        setMessages((prev) => [...prev, message]);
      }
    };
    SOCKET_CLIENT.on("NEW-MESSAGE", handleNewMessage);

    // Cleanup function
    return () => {
      console.log(`Leaving Room: ${roomID}`);
      SOCKET_CLIENT.emit("LEAVE-ROOM", { roomID });
      SOCKET_CLIENT.off("NEW-MESSAGE", handleNewMessage);
    };
  }, [roomID, receiverDetails?._id]);

  // 4. Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && e.shiftKey) {
      // Shift + Enter → allow new line (do nothing special)
      return; // let the textarea naturally add \n
    }
    if (e.key === "Enter" && !e.shiftKey) {
      // Plain Enter → send the message
      e.preventDefault(); // prevent adding extra newline before sending
      if (messageForm.values.message.trim()) {
        messageForm.handleSubmit();
      }
    }
  };

  if (!roomID) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100 bg-light text-muted">
        <i
          className="ti ti-message-dots"
          style={{ fontSize: "3rem", marginBottom: "10px" }}
        ></i>
        <p>Select a chat to begin messaging.</p>
      </div>
    );
  }

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };


  return (
    // Ensure this container is d-flex and takes full height of its parent
    <div className="d-flex flex-column h-100 w-100 p-0">
      {/* 1. Chat Header: Fixed height */}
      <div className="p-3 bg-white border-bottom shadow-sm d-flex justify-content-between align-items-center">
        <div className="d-flex gap-3 align-items-center">
          <figure className="avatar avatar-md m-0">
            <img
              src={receiverDetails?.profile_image || "placeholder.jpg"}
              alt="profile_image"
              className="rounded-circle"
              style={{ width: "40px", height: "40px" }}
            />
          </figure>
          <div className="chat-right-header-info">
            <h5 className="mb-0">{receiverDetails?.name || "Unknown User"}</h5>
          </div>
        </div>
      </div>

      {/* 2. Messages Area: Takes all remaining vertical space and scrolls */}
      {/* 2. Messages Area */}
      <div
        className="flex-grow-1 p-4 overflow-auto bg-light"
        ref={chatWindowRef}
      >
        <ul className="list-unstyled p-0 m-0">
          {(() => {
            let lastDate = null;
            return messages?.map((msg, idx) => {
              const msgDate = moment(msg.createdAt).format("YYYY-MM-DD");
              const showSeparator = msgDate !== lastDate;
              lastDate = msgDate;

              const isMyMsg = msg.senderId === SENDER._id;

              return (
                <React.Fragment key={idx}>
                  {showSeparator && (
                    <li className="d-flex justify-content-center my-4">
                      <span
                        className="badge bg-secondary text-white px-3 py-2 rounded-pill"
                        style={{ fontSize: "0.8rem", opacity: 0.8 }}
                      >
                        {formatDateSeparator(msg.createdAt)}
                      </span>
                    </li>
                  )}
                  <li
                    className={`d-flex ${isMyMsg ? "justify-content-end" : "justify-content-start"} mb-3`}
                  >
                    <div
                      className={`d-flex flex-column ${isMyMsg ? "align-items-end" : "align-items-start"}`}
                      style={{ width: "80%" }} // 💬 Take 80% of available width
                    >
                      <div
                        className={`chat-bubble ${isMyMsg ? "bg-primary text-white" : "bg-white border"} rounded-4 py-2 px-3 shadow-sm`}
                        style={{
                          width: "fit-content",
                          maxWidth: "100%",
                          wordBreak: "break-word",
                        }}
                      >
                        <pre
                          style={{
                            margin: 0,
                            padding: 0,
                            fontFamily: "inherit",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {msg.message}
                        </pre>
                        {/* <div
                                                    className="mb-0"
                                                    style={{
                                                        whiteSpace: "pre-wrap", // Preserves newlines AND wraps long lines
                                                        wordBreak: "break-word", // Breaks very long words if needed
                                                    }}
                                                >
                                                    {msg.message}
                                                </div> */}
                      </div>

                      <small
                        className="text-muted mt-1"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {moment(msg.createdAt).format("hh:mm A")}
                      </small>
                    </div>
                  </li>
                </React.Fragment>
              );
            });
          })()}
        </ul>
      </div>

      {/* 3. Chat Input: Fixed height */}
      <form onSubmit={messageForm.handleSubmit}>
        <div className="p-3 bg-white border-top">
          <div className="input-group">
            {/* <textarea
                            name="message"
                            className="form-control"
                            placeholder="Type your message here..."
                            value={messageForm.values.message}
                            onChange={messageForm.handleChange}
                            onBlur={messageForm.handleBlur}
                            onKeyDown={handleKeyPress}
                            style={{
                                resize: "none", // Prevent resizing
                                height: "auto", // Allow it to expand based on content
                            }}
                            rows="1"
                        /> */}
            <textarea
              name="message"
              className="form-control"
              placeholder="Type your message here..."
              value={messageForm.values.message}
              onChange={(e) => {
                messageForm.handleChange(e);
                // Auto adjust height
                e.target.style.height = "auto";
                e.target.style.height = `${e.target.scrollHeight}px`;
              }}
              onKeyDown={handleKeyPress}
              rows="1"
              style={{
                resize: "none",
                minHeight: "44px",
                maxHeight: "120px",
                overflowY: "auto",
              }}
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!messageForm.values.message.trim()}
            >
              Send 🚀
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
